import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const MissionListView = ({ missions, onMissionClick }) => {
  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return { name: 'CheckCircle', color: 'var(--color-success)' };
      case 'in-progress':
        return { name: 'PlayCircle', color: 'var(--color-primary)' };
      case 'locked':
        return { name: 'Lock', color: 'var(--color-muted-foreground)' };
      default:
        return { name: 'Circle', color: 'var(--color-muted-foreground)' };
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner':
        return 'text-success bg-success/10';
      case 'intermediate':
        return 'text-warning bg-warning/10';
      case 'advanced':
        return 'text-error bg-error/10';
      case 'boss':
        return 'text-secondary bg-secondary/10';
      default:
        return 'text-muted-foreground bg-muted/10';
    }
  };

  return (
    <div className="space-y-4">
      {missions?.map((mission) => {
        const statusIcon = getStatusIcon(mission?.status);
        const isClickable = mission?.status !== 'locked';

        return (
          <div
            key={mission?.id}
            className={`bg-card border border-subtle rounded-lg p-4 transition-all duration-200 ${
              isClickable 
                ? 'hover:shadow-elevation-1 hover:border-primary/50' 
                : 'opacity-60'
            }`}
          >
            <div className="flex items-start space-x-4">
              {/* Mission Thumbnail */}
              <div className="relative flex-shrink-0">
                <div className="w-16 h-16 rounded-lg overflow-hidden">
                  <Image
                    src={mission?.thumbnail}
                    alt={mission?.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -top-1 -right-1 w-5 h-5 bg-card rounded-full border border-background flex items-center justify-center">
                  <Icon name={statusIcon?.name} size={12} color={statusIcon?.color} />
                </div>
              </div>

              {/* Mission Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-1">{mission?.title}</h3>
                    <p className="text-sm text-muted-foreground line-clamp-2">{mission?.description}</p>
                  </div>
                  <div className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(mission?.difficulty)}`}>
                    {mission?.difficulty}
                  </div>
                </div>

                {/* Mission Stats */}
                <div className="flex items-center space-x-4 text-xs text-muted-foreground mb-3">
                  <div className="flex items-center">
                    <Icon name="Clock" size={12} className="mr-1" />
                    {mission?.estimatedTime}
                  </div>
                  <div className="flex items-center">
                    <Icon name="Star" size={12} className="mr-1" />
                    {mission?.points} points
                  </div>
                  <div className="flex items-center">
                    <Icon name="Users" size={12} className="mr-1" />
                    {mission?.completedBy} completed
                  </div>
                </div>

                {/* Progress Bar (for in-progress missions) */}
                {mission?.status === 'in-progress' && mission?.progress > 0 && (
                  <div className="mb-3">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-muted-foreground">Progress</span>
                      <span className="text-primary font-medium">{mission?.progress}%</span>
                    </div>
                    <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary transition-all duration-300"
                        style={{ width: `${mission?.progress}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    {mission?.tags && mission?.tags?.slice(0, 3)?.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-1 bg-muted/50 text-xs text-muted-foreground rounded"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {mission?.isBookmarked && (
                      <Icon name="Bookmark" size={16} color="var(--color-warning)" />
                    )}
                    <Button
                      variant={mission?.status === 'completed' ? "outline" : "default"}
                      size="sm"
                      disabled={!isClickable}
                      onClick={() => isClickable && onMissionClick(mission)}
                      iconName={
                        mission?.status === 'completed' ? 'RotateCcw' : 
                        mission?.status === 'in-progress' ? 'Play' : 'Lock'
                      }
                      iconPosition="left"
                      iconSize={14}
                    >
                      {mission?.status === 'completed' ? 'Review' : 
                       mission?.status === 'in-progress' ? 'Continue' : 'Locked'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default MissionListView;